/**
 * Add-risk form. State lives here so typing in the three editors does not
 * re-render the worksheet table behind the dialog.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import SkillDocumentCommentBox from "../../../../components/SkillDocumentCommentBox/SkillDocumentCommentBox";
import { RiskArea } from "../Types/RiskManagementTypes";
import { getDefaultFactors } from "./defaultFactors";
import { canonicalEditorHtml } from "./RiskWorksheetTable";

export interface AddRiskInput {
  area: string;
  contributor: string;
  ratingFactors: string;
  scenario: string;
  mitigation: string;
}

interface AddRiskDialogProps {
  areas: RiskArea[];
  initialAreaId: string;
  saving: boolean;
  getUsedContributorIds: (areaId: string) => Set<string>;
  onClose: () => void;
  onSubmit: (input: AddRiskInput) => void;
}

const factorsToHtml = (raw: string) => {
  const lines = raw.split("\n").filter((line) => line.trim());
  if (!lines.length) return "";
  return `<ul>${lines.map((line) => `<li><p>${line}</p></li>`).join("")}</ul>`;
};

const AddRiskDialog: React.FC<AddRiskDialogProps> = ({
  areas,
  initialAreaId,
  saving,
  getUsedContributorIds,
  onClose,
  onSubmit,
}) => {
  const [area, setArea] = useState(initialAreaId);
  const [contributor, setContributor] = useState("");
  const [rating, setRating] = useState("");
  const [defaultRating, setDefaultRating] = useState("");
  const [scenario, setScenario] = useState("");
  const [mitigation, setMitigation] = useState("");

  const ratingGetter = useRef<(() => string) | null>(null);
  const scenarioGetter = useRef<(() => string) | null>(null);
  const mitigationGetter = useRef<(() => string) | null>(null);

  const bindRating = useCallback((getter: (() => string) | null) => {
    ratingGetter.current = getter;
  }, []);
  const bindScenario = useCallback((getter: (() => string) | null) => {
    scenarioGetter.current = getter;
  }, []);
  const bindMitigation = useCallback((getter: (() => string) | null) => {
    mitigationGetter.current = getter;
  }, []);

  const onRatingChange = useCallback((html: string) => {
    setRating(canonicalEditorHtml(html));
  }, []);
  const onScenarioChange = useCallback((html: string) => {
    setScenario(canonicalEditorHtml(html));
  }, []);
  const onMitigationChange = useCallback((html: string) => {
    setMitigation(canonicalEditorHtml(html));
  }, []);

  const contributors = useMemo(
    () => areas.find((item) => item.id === area)?.contributors ?? [],
    [areas, area]
  );
  const usedIds = area ? getUsedContributorIds(area) : new Set<string>();

  useEffect(() => {
    if (!contributor) {
      setDefaultRating("");
      setRating("");
      return;
    }
    const contrib = contributors.find((item) => item.id === contributor);
    const areaObj = areas.find((item) => item.id === area);
    const raw =
      contrib?.factors ||
      (contrib && areaObj ? getDefaultFactors(areaObj.name, contrib.name) : "");
    const html = raw ? factorsToHtml(raw) : "";
    setDefaultRating(html);
    setRating(html);
  }, [contributor, contributors, areas, area]);

  const readField = (
    getter: React.MutableRefObject<(() => string) | null>,
    stored: string
  ) => canonicalEditorHtml(getter.current?.() ?? stored);

  const submit = () => {
    onSubmit({
      area,
      contributor,
      ratingFactors: readField(ratingGetter, rating),
      scenario: readField(scenarioGetter, scenario),
      mitigation: readField(mitigationGetter, mitigation),
    });
  };

  return (
    <Dialog
      open
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="lg"
      PaperProps={{ sx: { minHeight: "70vh" } }}
    >
      <DialogTitle>Add a new risk</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Risk Area</InputLabel>
            <Select
              label="Risk Area"
              value={area}
              onChange={(e) => {
                setArea(e.target.value as string);
                setContributor("");
              }}
            >
              {areas.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth disabled={!area}>
            <InputLabel>Contributor</InputLabel>
            <Select
              label="Contributor"
              value={contributor}
              onChange={(e) => setContributor(e.target.value as string)}
            >
              {contributors.map((item) => {
                const alreadyAdded = usedIds.has(item.id);
                return (
                  <MenuItem
                    key={item.id}
                    value={item.id}
                    disabled={alreadyAdded}
                    sx={
                      alreadyAdded
                        ? { opacity: 0.45, fontStyle: "italic" }
                        : undefined
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <span>{item.name}</span>
                      {alreadyAdded && (
                        <Typography
                          variant="caption"
                          color="text.disabled"
                          sx={{ ml: "auto" }}
                        >
                          already added
                        </Typography>
                      )}
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 0.5, display: "block" }}
            >
              Factors for rating Impact &amp; Probability
            </Typography>
            <SkillDocumentCommentBox
              compact
              hideTableControls
              commitOnBlur
              value={rating}
              onChange={onRatingChange}
              bindHtmlGetter={bindRating}
              placeholder={
                contributor
                  ? "Edit or add factors..."
                  : "Select a contributor to load default factors..."
              }
              toolbarEnd={
                defaultRating ? (
                  <Tooltip title="Reset to default factors">
                    <IconButton
                      size="small"
                      onClick={() => setRating(defaultRating)}
                      sx={{ ml: 0.5 }}
                    >
                      <RestartAltIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null
              }
            />
          </Box>
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 0.5, display: "block" }}
            >
              Specify Project Scenario justifying the impact and probability
            </Typography>
            <SkillDocumentCommentBox
              compact
              hideTableControls
              commitOnBlur
              value={scenario}
              onChange={onScenarioChange}
              bindHtmlGetter={bindScenario}
              placeholder="Describe the scenario..."
            />
          </Box>
          <Box>
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ mb: 0.5, display: "block" }}
            >
              Mitigation plan
            </Typography>
            <SkillDocumentCommentBox
              compact
              hideTableControls
              commitOnBlur
              value={mitigation}
              onChange={onMitigationChange}
              bindHtmlGetter={bindMitigation}
              placeholder="Mitigation plan..."
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving || !area || !contributor}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Add risk
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddRiskDialog;
